import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import api from './services/api';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import Projects from './pages/Projects';
import Tickets from './pages/Tickets';
import Masters from './pages/Masters';
import Teams from './pages/Teams';
import Assignments from './pages/Assignments';
import Users from './pages/Users';
import Login from './pages/Login';
import SsoLogin from './pages/SsoLogin';
import { isAdmin } from './utils/role';

function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  const changePassword = async () => {
    setPwdError(''); setPwdSuccess('');
    if (!pwdForm.currentPassword || !pwdForm.newPassword) { setPwdError('All fields are required'); return; }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) { setPwdError('New passwords do not match'); return; }
    if (pwdForm.newPassword.length < 6) { setPwdError('Password must be at least 6 characters'); return; }
    setPwdSaving(true);
    try {
      await api.post('/auth/change-password', { currentPassword: pwdForm.currentPassword, newPassword: pwdForm.newPassword });
      setPwdSuccess('Password changed successfully!');
      setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => { setShowChangePwd(false); setPwdSuccess(''); }, 1500);
    } catch (e) {
      setPwdError(e.response?.data?.error || 'Failed to change password');
    } finally { setPwdSaving(false); }
  };

  const links = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { path: '/companies', label: 'Companies', icon: '🏢' },
    { path: '/projects', label: 'Projects', icon: '📁' },
    { path: '/tickets', label: 'Tickets', icon: '🎫' },
    ...(isAdmin() ? [
      { path: '/assignments', label: 'Assignments', icon: '🧩' },
      { path: '/teams', label: 'Teams', icon: '🧑‍🤝‍🧑' },
      { path: '/masters', label: 'Masters', icon: '⚙️' },
      { path: '/users', label: 'Users', icon: '👥' },
    ] : []),
  ];

  const logout = () => {
    // If the user arrived via the CLTS portal (SSO), return to the launcher.
    // Otherwise show this app's own login.
    const viaPortal = localStorage.getItem('loginSource') === 'portal';
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('loginSource');
    if (viaPortal) {
      window.location.href = 'https://app.cloudleaders.co.in';
    } else {
      navigate('/login');
    }
  };

  return (
    <aside style={{
      width: 'var(--sidebar-width)', height: '100vh', position: 'fixed', left: 0, top: 0,
      background: 'linear-gradient(180deg, #2e1065 0%, #1e293b 100%)',
      color: '#fff', display: 'flex', flexDirection: 'column', zIndex: 100
    }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.5 }}>🎧 CLTS Support</div>
        <div style={{ fontSize: 12, color: '#a5b4fc', marginTop: 2 }}>Center</div>
      </div>

      <nav style={{ flex: 1, padding: '12px', overflowY: 'auto' }}>
        {links.map(l => (
          <Link key={l.path} to={l.path} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
            borderRadius: 8, color: location.pathname === l.path ? '#fff' : '#c4b5fd',
            background: location.pathname === l.path ? 'rgba(124,58,237,0.35)' : 'transparent',
            marginBottom: 4, fontSize: 14, fontWeight: 500, textDecoration: 'none',
            transition: 'all 0.15s'
          }}>
            <span style={{ fontSize: 18 }}>{l.icon}</span> {l.label}
          </Link>
        ))}
      </nav>

      {showChangePwd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: 4, color: '#0f172a' }}>Change Password</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Logged in as <strong>{user.username}</strong></p>
            {pwdError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{pwdError}</div>}
            {pwdSuccess && <div style={{ background: '#dcfce7', color: '#15803d', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{pwdSuccess}</div>}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Current Password</label>
              <input type="password" value={pwdForm.currentPassword} onChange={e => setPwdForm({ ...pwdForm, currentPassword: e.target.value })} placeholder="Enter current password" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>New Password</label>
              <input type="password" value={pwdForm.newPassword} onChange={e => setPwdForm({ ...pwdForm, newPassword: e.target.value })} placeholder="Min 6 characters" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Confirm New Password</label>
              <input type="password" value={pwdForm.confirmPassword} onChange={e => setPwdForm({ ...pwdForm, confirmPassword: e.target.value })} placeholder="Re-enter new password" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={changePassword} disabled={pwdSaving} style={{ flex: 1 }}>{pwdSaving ? 'Saving...' : 'Change Password'}</button>
              <button className="btn-ghost" onClick={() => { setShowChangePwd(false); setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); setPwdError(''); }} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: 13, color: '#e0e7ff' }}>{user.fullName || 'User'}</div>
        <div style={{ fontSize: 11, color: '#a5b4fc', marginBottom: 8 }}>{user.role}</div>
        <button onClick={() => { setShowChangePwd(true); setPwdError(''); setPwdSuccess(''); }} style={{
          width: '100%', padding: '6px 12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
          color: '#c4b5fd', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginBottom: 6
        }}>🔑 Change Password</button>
        <button onClick={logout} style={{
          width: '100%', padding: '6px 12px', background: 'rgba(255,255,255,0.1)',
          color: '#c4b5fd', borderRadius: 6, fontSize: 12, border: 'none', cursor: 'pointer'
        }}>Logout</button>
      </div>
    </aside>
  );
}

function Layout({ children }) {
  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <main style={{ marginLeft: 'var(--sidebar-width)', flex: 1, padding: 24, minHeight: '100vh' }}>
        {children}
      </main>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? <Layout>{children}</Layout> : <Navigate to="/login" />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/sso" element={<SsoLogin />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/companies" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
        <Route path="/tickets" element={<ProtectedRoute><Tickets /></ProtectedRoute>} />
        <Route path="/assignments" element={<ProtectedRoute><Assignments /></ProtectedRoute>} />
        <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
        <Route path="/masters" element={<ProtectedRoute><Masters /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
      </Routes>
    </BrowserRouter>
  );
}
